import React, { useState, useEffect, useRef } from "react";
import { toast, ToastContainer } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import {
  startOfMonth,
  endOfMonth,
  addDays,
  format,
  isSameDay,
  addMonths,
  startOfWeek,
  endOfWeek,
  addWeeks,
  differenceInCalendarDays,
  sub,
} from "date-fns";
import { FaCalendarAlt, FaChevronLeft, FaChevronRight, FaHome } from "react-icons/fa";
import axios from "axios";
import EmployeeNavbar from "../Layout/EmployeeNavbar";
import API_BASE_URL from "../config";

/**
 * TimesheetGrid (updated)
 * - Adds Jira-style action modal when clicking a cell with existing logs:
 *    -> shows logs in that cell with Edit / Split / Delete actions
 * - Edit reuses the Log Time modal (prefills and sets editWorklogId)
 * - Split deducts hours from original and creates new worklog (simple split flow)
 * - Delete calls DELETE API and updates state
 *
 * NOTE: adapt fetch/endpoint details if your backend expects different payloads.
 */

const TimesheetGrid = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const employeeId = location.state?.employeeId;

  const [worklogs, setWorklogs] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [employeeName, setEmployeeName] = useState("Employee");
  const [viewMode, setViewMode] = useState("monthly");
  const [billableFilter, setBillableFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(() => startOfMonth(new Date()));
  const [endDate, setEndDate] = useState(() => endOfMonth(new Date()));
  const [employeePhotoPath, setEmployeePhotoPath] = useState("");
  const [error, setError] = useState("");
  const [holidayDates, setHolidayDates] = useState([]);
  const [holidayMap, setHolidayMap] = useState({});
  const [weeklyHolidays, setWeeklyHolidays] = useState([]);
  const invalidDateToastShown = useRef(false);
  const [employees, setEmployees] = useState([]);
  const [leaveDates, setLeaveDates] = useState([]);

  // New: action modal & split modal state
  const [showActionModal, setShowActionModal] = useState(false);
  const [cellLogs, setCellLogs] = useState([]); // logs in clicked cell
  const [cellTask, setCellTask] = useState(null); // task of the clicked cell
  const [cellDate, setCellDate] = useState(null); // date of clicked cell

  const [showSplitModal, setShowSplitModal] = useState(false);
  const [splitOriginalLog, setSplitOriginalLog] = useState(null);
  const [splitAmount, setSplitAmount] = useState("");

  const [durationRaw, setDurationRaw] = useState("");     // what user types / what input shows
  const [durationDecimal, setDurationDecimal] = useState(0); // numeric hours for API
  const [durationReadable, setDurationReadable] = useState(""); // 1h 30m etc


  // Logged-in user token
  const token = sessionStorage.getItem("token");

  // Active task selected
  const [activeTask, setActiveTask] = useState(null);

  // Worklog editing (null means "new worklog")
  const [editWorklogId, setEditWorklogId] = useState(null);
  const [showLogForm, setShowLogForm] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState({
    taskId: null,
    taskTitle: "",
    date: format(new Date(), "yyyy-MM-dd"),
    hours: "",
    billingType: "billable",
    description: "",
    logAnother: false,
  });

  // ------------------- data fetching -------------------
  useEffect(() => {
    const token = sessionStorage.getItem("token");
    if (!token) {
      navigate("/");
      return;
    }

    const fetchEmployeeAndWorklogs = async () => {
      if (!employeeId) return;
      setLoading(true);
      try {
        const empRes = await axios.get(`${API_BASE_URL}/api/employees/${employeeId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const empData = empRes.data;
        setEmployeeName(`${empData.firstName} ${empData.lastName}`);
        setEmployeePhotoPath(empData.photoPath || "");

        const logsRes = await axios.get(`${API_BASE_URL}/api/worklogs/employee/${employeeId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setWorklogs(Array.isArray(logsRes.data) ? logsRes.data : []);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(
          err.response?.status === 403
            ? "You are not authorized to view this data."
            : "Unable to fetch timesheet data."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchEmployeeAndWorklogs();
  }, [employeeId, navigate]);

  useEffect(() => {
    // load bootstrap links dynamically (kept from your version)
    const bootstrapLink = document.createElement("link");
    bootstrapLink.rel = "stylesheet";
    bootstrapLink.href = "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css";
    bootstrapLink.id = "timesheetgrid-bootstrap";
    document.head.appendChild(bootstrapLink);

    const iconsLink = document.createElement("link");
    iconsLink.rel = "stylesheet";
    iconsLink.href = "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css";
    iconsLink.id = "timesheetgrid-icons";
    document.head.appendChild(iconsLink);

    return () => {
      const existingBootstrap = document.getElementById("timesheetgrid-bootstrap");
      if (existingBootstrap) existingBootstrap.remove();
      const existingIcons = document.getElementById("timesheetgrid-icons");
      if (existingIcons) existingIcons.remove();
    };
  }, []);

  useEffect(() => {
    fetchWeeklyHolidays();
    // fetch public holidays
    fetchHolidays();
  }, []);

  async function fetchWeeklyHolidays() {
    try {
      const res = await axios.get("/api/holidays/weekly");
      const days = res.data.map((h) => h.name);
      setWeeklyHolidays(days);
    } catch (err) {
      console.error("Failed to fetch weekly holidays:", err);
    }
  }

  async function fetchHolidays() {
    try {
      const res = await fetch(`${API_BASE_URL}/api/holidays`);
      const data = await res.json();

      const allHolidayDates = [];
      const holidayNameMap = {};

      data.forEach((h) => {
        const start = new Date(h.startDate);
        const end = new Date(h.endDate);
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
          const formatted = d.toISOString().split("T")[0];
          allHolidayDates.push(formatted);
          holidayNameMap[formatted] = h.name;
        }
      });

      setHolidayDates(allHolidayDates);
      setHolidayMap(holidayNameMap);
    } catch (err) {
      console.error("Error fetching holidays:", err);
    }
  }

  // fetch tasks
  useEffect(() => {
    const token = sessionStorage.getItem("token");
    if (!employeeId || !token) return;
    const fetchTasks = async () => {
      try {
        const res = await axios.get(
          `${API_BASE_URL}/api/worklogs/employee/${employeeId}/tasks`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setTasks(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Error fetching tasks:", err);
      }
    };
    fetchTasks();
  }, [employeeId]);



  // complementary employee name fetch (kept)
  useEffect(() => {
    if (!employeeId) return;
    const fetchEmployeeName = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/employees/${employeeId}`);
        const data = await res.json();
        setEmployeeName(`${data.firstName} ${data.lastName}`);
      } catch (err) {
        console.error("Error fetching employee name:", err);
      }
    };
    fetchEmployeeName();
  }, [employeeId]);



  const openCreateLogModal = (date, taskId, taskTitle) => {
    setModalData({
      date: date,
      taskId: taskId,
      taskTitle: taskTitle,
      hours: "",
      billingType: "billable",
      description: "",
      logAnother: false,
    });

    // clear duration states when creating a new log
    if (typeof setDurationRaw === "function") setDurationRaw("");
    if (typeof setDurationReadable === "function") setDurationReadable("");
    if (typeof setDurationDecimal === "function") setDurationDecimal(0);

    setShowModal(true);
  };



  // format decimal like 1.5 -> "1h 30m"
  // convert decimal hours like 1.5 -> "1h 30m"
  const formatDecimalToReadable = (decimal) => {
    if (!decimal || isNaN(decimal) || decimal <= 0) return "";
    const hours = Math.floor(decimal);
    const minutes = Math.round((decimal - hours) * 60);
    const parts = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    return parts.join(" ") || "0m";
  };

  // convert many possible stored values into decimal hours
  const ensureDecimalHours = (value) => {
    if (value == null) return 0;

    // numeric
    if (typeof value === "number") {
      return value > 24 ? Number((value / 60).toFixed(2)) : Number(value.toFixed(2));
    }

    const s = String(value).trim().toLowerCase();
    if (!s) return 0;

    // "90m" -> minutes
    if (s.endsWith("m")) {
      const n = Number(s.replace("m", "").trim());
      return Number((n / 60).toFixed(2));
    }

    // contains h or m: "1h 30m"
    if (/[hm]/.test(s)) {
      const hourMatch = s.match(/(\d+(?:\.\d+)?)\s*h/);
      const minuteMatch = s.match(/(\d+(?:\.\d+)?)\s*m/);
      const hours = hourMatch ? parseFloat(hourMatch[1]) : 0;
      const minutes = minuteMatch ? parseFloat(minuteMatch[1]) : 0;
      return Number((hours + minutes / 60).toFixed(2));
    }

    // "1:30" format
    if (s.includes(":")) {
      const [hStr, mStr] = s.split(":");
      const h = Number(hStr || 0);
      const m = Number(mStr || 0);
      return Number((h + m / 60).toFixed(2));
    }

    // decimal string, or minutes if >24
    const n = Number(s.replace(",", "."));
    if (!Number.isNaN(n)) return n > 24 ? Number((n / 60).toFixed(2)) : Number(n.toFixed(2));

    return 0;
  };



  const parseDurationToDecimal = (input) => {
    if (!input) return 0;
    const s = String(input).trim().toLowerCase();

    // Format like "1h 30m"
    if (/[hm]/.test(s)) {
      const hourMatch = s.match(/(\d+(?:\.\d+)?)\s*h/);
      const minuteMatch = s.match(/(\d+(?:\.\d+)?)\s*m/);

      const hours = hourMatch ? parseFloat(hourMatch[1]) : 0;
      const minutes = minuteMatch ? parseFloat(minuteMatch[1]) : 0;

      return Number((hours + minutes / 60).toFixed(2));
    }

    // Decimal
    const num = Number(s.replace(",", "."));
    return !isNaN(num) ? Number(num.toFixed(2)) : 0;
  };

  const handleDurationChange = (raw) => {
    setDurationRaw(raw);

    const decimal = parseDurationToDecimal(raw);
    setDurationDecimal(decimal);

    const readable = formatDecimalToReadable(decimal);
    setDurationReadable(readable);
  };

  const handleDurationBlur = () => {
    const decimal = durationDecimal;
    const readable = formatDecimalToReadable(decimal);

    setDurationRaw(readable); // show readable in input
  };






  // ------------------- utilities -------------------
  const isWeeklyHoliday = (day) => {
    const dayName = format(day, "EEEE");
    return weeklyHolidays.includes(dayName);
  };

  const isHolidayColumn = (day) => {
    const formatted = format(day, "yyyy-MM-dd");
    return holidayDates.includes(formatted);
  };

  // filtered worklogs by date and billable filter
  const filteredWorklogs = (() => {
    if (!startDate && !endDate) return worklogs;

    const from = startDate ? new Date(startDate) : null;
    const to = endDate ? new Date(endDate) : null;

    if (from && to && to < from) {
      if (!invalidDateToastShown.current) {
        toast.error("End date must be greater than or equal to Start date.");
        invalidDateToastShown.current = true;
      }
      return worklogs;
    } else {
      invalidDateToastShown.current = false;
    }

    return worklogs.filter((log) => {
      const logDate = new Date(log.date);
      const inStart = from ? logDate >= from : true;
      const inEnd = to ? logDate <= to : true;
      if (!(inStart && inEnd)) return false;
      if (billableFilter === "all") return true;
      return log.billingType === billableFilter;
    });
  })();

  const getHours = (taskId, day, billableType) =>
    filteredWorklogs
      .filter(
        (log) =>
          log.taskId === taskId &&
          isSameDay(new Date(log.date), day) &&
          (!billableType || log.billingType === billableType)
      )
      .reduce((sum, log) => sum + log.hours, 0);

  const totalHours = filteredWorklogs.reduce((sum, log) => sum + log.hours, 0);
  const totalBillable = filteredWorklogs
    .filter((log) => log.billingType === "billable")
    .reduce((sum, log) => sum + log.hours, 0);
  const totalNonBillable = totalHours - totalBillable;

  const totalFilteredHours = totalHours;
  const totalFilteredBillable = totalBillable;
  const totalFilteredNonBillable = filteredWorklogs
    .filter((log) => log.billingType === "non-billable")
    .reduce((sum, log) => sum + log.hours, 0);

  // ------------------- navigation handlers -------------------
  const handlePrev = () => {
    if (viewMode === "monthly") {
      const newStart = sub(startDate, { months: 1 });
      setStartDate(newStart);
      setEndDate(endOfMonth(newStart));
    } else {
      const newStart = sub(startDate, { weeks: 1 });
      setStartDate(newStart);
      setEndDate(endOfWeek(newStart, { weekStartsOn: 1 }));
    }
  };

  const handleNext = () => {
    if (viewMode === "monthly") {
      const newStart = addMonths(startDate, 1);
      setStartDate(newStart);
      setEndDate(endOfMonth(newStart));
    } else {
      const newStart = addWeeks(startDate, 1);
      setStartDate(newStart);
      setEndDate(endOfWeek(newStart, { weekStartsOn: 1 }));
    }
  };

  // days array for grid
  const dayCount = differenceInCalendarDays(endDate, startDate) + 1;
  const days = Array.from({ length: dayCount }, (_, i) => addDays(startDate, i));

  // ------------------- cell click: create vs action -------------------
  const handleCellClick = (task, day) => {
    const logsInCell = filteredWorklogs.filter(
      (log) => log.taskId === task.id && isSameDay(new Date(log.date), day)
    );

    if (!logsInCell.length) {
      // no logs -> open create modal (existing behavior)
      setEditWorklogId(null);
      setModalData({
        taskId: task.id,
        taskTitle: task.title,
        date: format(day, "yyyy-MM-dd"),
        hours: "",
        billingType: "billable",
        description: "",
        logAnother: false,
      });
      setShowModal(true);
      return;
    }

    // have logs -> open action modal (Jira-style)
    setCellLogs(logsInCell);
    setCellTask(task);
    setCellDate(format(day, "yyyy-MM-dd"));
    setShowActionModal(true);
  };


  const editWorklog = (log) => {
    // Convert duration from whatever the API returned into decimal + readable
    const decimal = ensureDecimalHours(log.hours ?? log.duration ?? log.minutes ?? log.time ?? log.loggedHours ?? 0);
    const readable = formatDecimalToReadable(decimal);

    // Populate modalData fields
    setModalData((prev) => ({
      ...prev,
      taskId: log.taskId ?? prev.taskId ?? null,
      taskTitle: log.taskTitle ?? log.taskName ?? prev.taskTitle ?? "",
      taskCode: log.taskCode ?? prev.taskCode ?? "",
      description: log.description ?? log.notes ?? prev.description ?? "",
      billingType: log.billingType ?? prev.billingType ?? "billable",
      date:
        (log.date && String(log.date).includes("T") ? String(log.date).split("T")[0] : log.date) ||
        (log.loggedAt && String(log.loggedAt).includes("T") ? String(log.loggedAt).split("T")[0] : log.loggedAt) ||
        prev.date ||
        new Date().toISOString().split("T")[0],
      hours: decimal, // keep for compatibility (not used for saving if you use durationDecimal)
    }));

    // Populate the duration states your input uses
    if (typeof setDurationDecimal === "function") setDurationDecimal(decimal);
    if (typeof setDurationReadable === "function") setDurationReadable(readable);
    if (typeof setDurationRaw === "function") setDurationRaw(readable); // input is bound to durationRaw

    // set editing id and show modal
    setEditWorklogId(log.id);
    setShowModal(true);
  };



  // ------------------- save (create/update) worklog -------------------
  const saveWorklog = async () => {
    // --- basic client-side validation ---
    if (!modalData.description?.trim() || !durationDecimal || !modalData.date || !modalData.taskTitle) {
      toast.error("Please fill required fields.");
      return;
    }

    // ensure numeric hours
    const hoursToSend = Number(durationDecimal);
    if (!hoursToSend || Number.isNaN(hoursToSend) || hoursToSend <= 0) {
      toast.error("Please enter a valid duration (e.g. 0.5 or 30m).");
      return;
    }

    // token / employee fallback
    const tokenLocal = typeof token !== "undefined" && token ? token : sessionStorage.getItem("token");
    const employeeIdLocal =
      typeof employeeId !== "undefined" && employeeId ? employeeId : sessionStorage.getItem("employeeId");

    if (!employeeIdLocal) {
      console.warn("employeeId not available in scope; using sessionStorage fallback", employeeIdLocal);
    }

    const payload = {
      employeeId: Number(employeeIdLocal || 0),
      taskId: modalData.taskId,
      taskCode: modalData.taskCode || modalData.taskTitle || activeTask?.taskCode || "",
      description: modalData.description.trim(),
      hours: hoursToSend,
      billingType: modalData.billingType,
      date: modalData.date,
    };

    try {
      let res;
      const headers = {
        "Content-Type": "application/json",
        ...(tokenLocal && { Authorization: "Bearer " + tokenLocal }),
      };

      if (editWorklogId) {
        res = await fetch(`${API_BASE_URL}/api/worklogs/${editWorklogId}`, {
          method: "PUT",
          headers,
          body: JSON.stringify(payload),
        });
      } else {
        res = await fetch(`${API_BASE_URL}/api/worklogs`, {
          method: "POST",
          headers,
          body: JSON.stringify(payload),
        });
      }

      if (res.ok) {
        const updatedLog = await res.json();

        setWorklogs((prev) => {
          if (editWorklogId) {
            return prev.map((w) => (w.id === editWorklogId ? updatedLog : w));
          } else {
            return [...prev, updatedLog];
          }
        });

        // Reset editing state and modalData
        setEditWorklogId(null);

        setModalData({
          taskId: null,
          taskTitle: "",
          date: format(new Date(), "yyyy-MM-dd"),
          hours: "", // still present for compatibility but not used for sending
          billingType: "billable",
          description: "",
          logAnother: false,
        });

        // Reset duration-specific states (you must have these in component)
        if (typeof setDurationRaw === "function") setDurationRaw("");
        if (typeof setDurationReadable === "function") setDurationReadable("");
        if (typeof setDurationDecimal === "function") setDurationDecimal(0);

        // If user didn't choose "log another", close modal
        if (!modalData.logAnother) {
          setShowModal(false);
        }

        toast.success(editWorklogId ? "Worklog updated." : "Worklog created.");
      } else {
        const text = await res.text();
        console.error("Failed to save worklog:", text);
        toast.error("Failed to save worklog.");
      }
    } catch (err) {
      console.error("Error saving worklog:", err);
      toast.error("Error saving worklog.");
    }
  };


  // ------------------- delete worklog -------------------
  const deleteWorklog = async (logId) => {
    if (!window.confirm("Delete this worklog? This action cannot be undone.")) return;

    try {
      const res = await fetch(`${API_BASE_URL}/api/worklogs/${logId}`, {
        method: "DELETE",
        headers: { Authorization: "Bearer " + token },
      });

      if (res.ok) {
        setWorklogs((prev) => prev.filter((w) => w.id !== logId));
        toast.success("Worklog deleted.");
        // refresh cell logs shown in action modal
        setCellLogs((prev) => prev.filter((l) => l.id !== logId));
        if (cellLogs.length <= 1) {
          setShowActionModal(false);
        }
      } else {
        const txt = await res.text();
        console.error("Delete failed:", txt);
        toast.error("Failed to delete worklog.");
      }
    } catch (err) {
      console.error("Error deleting worklog:", err);
      toast.error("Error deleting worklog.");
    }
  };

  // ------------------- edit from action modal -------------------
  const editWorklogFromCell = (log) => {
    // Reuse the existing editWorklog helper so it populates modalData + duration states
    if (typeof editWorklog === "function") {
      editWorklog(log);
    } else {
      // fallback: populate minimal fields if editWorklog isn't available
      setModalData({
        taskId: log.taskId,
        taskTitle: cellTask?.title || "",
        date: format(new Date(log.date), "yyyy-MM-dd"),
        hours: String(log.hours),
        billingType: log.billingType,
        description: log.description || "",
        logAnother: false,
      });

      const decimal = ensureDecimalHours(log.hours ?? log.duration ?? 0);
      const readable = formatDecimalToReadable(decimal);
      if (typeof setDurationDecimal === "function") setDurationDecimal(decimal);
      if (typeof setDurationReadable === "function") setDurationReadable(readable);
      if (typeof setDurationRaw === "function") setDurationRaw(readable);

      setEditWorklogId(log.id);
      setShowModal(true);
    }

    // close the action modal
    setShowActionModal(false);
  };


  // ------------------- split flow -------------------
  const openSplitModal = (log) => {
    setSplitOriginalLog(log);
    setSplitAmount(""); // empty
    setShowSplitModal(true);
    setShowActionModal(false);
  };

  const performSplit = async () => {
    // splitAmount => hours for new log (must be >0 and < original hours)
    const splitNum = Number(splitAmount);
    if (!splitOriginalLog) return;
    if (!splitNum || splitNum <= 0) {
      toast.error("Enter an amount greater than 0.");
      return;
    }
    if (splitNum >= splitOriginalLog.hours) {
      toast.error("Split amount must be less than original hours.");
      return;
    }

    // Create new worklog with split hours, same task, same date, same billingType (user can edit after)
    const newPayload = {
      employeeId: employeeId,
      taskId: splitOriginalLog.taskId,
      description: (splitOriginalLog.description || "") + " (split)",
      hours: splitNum,
      billingType: splitOriginalLog.billingType,
      date: format(new Date(splitOriginalLog.date), "yyyy-MM-dd"),
    };

    try {
      // 1) Create new worklog
      const createRes = await fetch(`${API_BASE_URL}/api/worklogs`, {
        method: "POST",
        headers: {
          Authorization: "Bearer " + token,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(newPayload),
      });

      if (!createRes.ok) {
        const txt = await createRes.text();
        console.error("Failed to create split:", txt);
        toast.error("Failed to create split worklog.");
        return;
      }

      const created = await createRes.json();

      // 2) Update original worklog to reduce hours
      const updatedPayload = {
        ...splitOriginalLog,
        hours: Number(splitOriginalLog.hours) - splitNum,
        description: splitOriginalLog.description || "",
        billingType: splitOriginalLog.billingType,
        taskId: splitOriginalLog.taskId,
        employeeId: splitOriginalLog.employeeId || employeeId,
        date: format(new Date(splitOriginalLog.date), "yyyy-MM-dd"),
      };

      // ensure no id field in body if backend rejects it; we use PUT to id endpoint
      const updateRes = await fetch(`${API_BASE_URL}/api/worklogs/${splitOriginalLog.id}`, {
        method: "PUT",
        headers: {
          Authorization: "Bearer " + token,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          employeeId: updatedPayload.employeeId,
          taskId: updatedPayload.taskId,
          description: updatedPayload.description,
          hours: updatedPayload.hours,
          billingType: updatedPayload.billingType,
          date: updatedPayload.date,
        }),
      });

      if (!updateRes.ok) {
        const txt = await updateRes.text();
        console.error("Failed to update original after split:", txt);
        toast.error("Split created but failed to update original log.");
        // still add created log to UI
        setWorklogs((prev) => [...prev, created]);
        setShowSplitModal(false);
        return;
      }

      const updatedOriginal = await updateRes.json();

      // update UI: replace original and add created
      setWorklogs((prev) =>
        prev
          .map((w) => (w.id === updatedOriginal.id ? updatedOriginal : w))
          .concat(created)
      );

      toast.success("Split completed.");
      setShowSplitModal(false);
    } catch (err) {
      console.error("Error during split:", err);
      toast.error("Error performing split.");
    }
  };


  const getEmployeeName = (id) => {
    const emp = employees.find(e => e.id === id);
    return emp ? emp.name : "—";
  };

  const getTaskName = (id) => {
    const task = tasks.find(t => t.id === id);
    return task ? task.title : "—";
  };

  const fetchLeaves = async () => {
    const token = sessionStorage.getItem("token");

    if (!token) {
      navigate("/");
      return;
    }

    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/leaves/my-leaves`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Convert API result into a yyyy-MM-dd list like before
      const leaveList = [];

      response.data.forEach((lv) => {
        const start = new Date(lv.startDate);
        const end = new Date(lv.endDate);

        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
          leaveList.push(d.toISOString().split("T")[0]);
        }
      });

      setLeaveDates(leaveList);
    } catch (error) {
      console.error("Error fetching leaves:", error);
      setError(
        error.response?.status === 403
          ? "You are not authorized to view this data."
          : "Unable to fetch leave data."
      );
    }
  };

  useEffect(() => {
    fetchLeaves();
  }, []); // runs once

  const isLeaveDay = (day) => {
    const formatted = format(day, "yyyy-MM-dd");
    return leaveDates.includes(formatted);
  };

  //console.log("LOG DATA →", cellLogs);

  // ------------------- JSX / rendering -------------------
  if (loading)
    return (
      <div className="d-flex justify-content-center align-items-center mt-5">
        <div className="spinner-border text-primary"></div>
        <span className="ms-2 fs-5">Loading timesheet...</span>
      </div>
    );

  return (
    <>
      <ToastContainer position="top-right" autoClose={4000} />
      <div className="fixed-top">
        <EmployeeNavbar />
      </div>
      <div className="container-fluid my-4" style={{ paddingTop: "110px" }}>
        {/* Header / Controls (kept compact, modern) */}
        <div className="d-flex flex-wrap justify-content-between align-items-center p-3 bg-white rounded shadow-sm border gap-3">
          <div className="d-flex align-items-center gap-3">
            <img
              src={employeePhotoPath ? `/uploads/${employeePhotoPath}` : "/default-avatar.png"}
              alt={employeeName}
              className="rounded-circle border border-2 border-primary"
              style={{ width: "50px", height: "50px", objectFit: "cover" }}
            />
            <div className="d-flex flex-column">
              <h6 className="fw-bold text-primary mb-0">{employeeName}</h6>
              <small className="text-muted">Employee ID: {employeeId}</small>
            </div>
          </div>

          <div className="d-flex align-items-center gap-2 flex-wrap">
            <button
              className={`btn btn-sm ${viewMode === "monthly" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => {
                setViewMode("monthly");
                setStartDate(startOfMonth(startDate));
                setEndDate(endOfMonth(startDate));
              }}
            >
              Monthly
            </button>

            <button
              className={`btn btn-sm ${viewMode === "weekly" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => {
                const today = new Date();
                setViewMode("weekly");
                setStartDate(startOfWeek(today, { weekStartsOn: 1 }));
                setEndDate(endOfWeek(today, { weekStartsOn: 1 }));
              }}
            >
              Weekly
            </button>

            <div className="btn-group btn-group-sm">
              <button
                className={`btn btn-sm ${billableFilter === "all" ? "btn-success" : "btn-outline-success"}`}
                onClick={() => setBillableFilter("all")}
              >
                All
              </button>
              <button
                className={`btn btn-sm ${billableFilter === "billable" ? "btn-success" : "btn-outline-success"}`}
                onClick={() => setBillableFilter("billable")}
              >
                Billable
              </button>
              <button
                className={`btn btn-sm ${billableFilter === "non-billable" ? "btn-success" : "btn-outline-success"}`}
                onClick={() => setBillableFilter("non-billable")}
              >
                Non-Billable
              </button>
            </div>

            <div className="btn-group btn-group-sm">
              <button className="btn btn-outline-primary" onClick={handlePrev}>
                <FaChevronLeft />
              </button>
              <button className="btn btn-outline-primary" onClick={handleNext}>
                <FaChevronRight />
              </button>
            </div>

            <div className="d-flex gap-1 align-items-center">
              <input
                type="date"
                value={startDate ? format(startDate, "yyyy-MM-dd") : ""}
                onChange={(e) => setStartDate(e.target.value ? new Date(e.target.value) : null)}
                className="form-control form-control-sm"
                style={{ width: "110px" }}
              />
              <span className="text-muted">to</span>
              <input
                type="date"
                value={endDate ? format(endDate, "yyyy-MM-dd") : ""}
                onChange={(e) => {
                  const selected = e.target.value ? new Date(e.target.value) : null;
                  if (selected && startDate && selected < startDate) {
                    const today = new Date();
                    setEndDate(today);
                    toast.error("End date cannot be earlier than Start date. Reset to today.");
                  } else {
                    setEndDate(selected);
                  }
                }}
                className="form-control form-control-sm"
                style={{ width: "110px" }}
              />
            </div>
          </div>
        </div>

        {/* Summary cards */}
        <div className="row g-2 mb-3 mt-2">
          {[
            { title: "Total Hours", value: totalFilteredHours, bg: "primary", icon: "bi-hourglass-split" },
            { title: "Billable", value: totalFilteredBillable, bg: "success", icon: "bi-cash-coin" },
            { title: "Non-Billable", value: totalFilteredNonBillable, bg: "danger", icon: "bi-slash-circle" },
          ].map((card, idx) => (
            <div key={idx} className="col-12 col-sm-4 col-md-2">
              <div className="card shadow-sm border-0 h-100 p-2">
                <div className="d-flex align-items-center gap-2">
                  <div
                    className={`rounded-circle bg-${card.bg} bg-opacity-10 text-${card.bg} d-flex align-items-center justify-content-center`}
                    style={{ width: "35px", height: "35px" }}
                  >
                    <i className={`bi ${card.icon} fs-6`}></i>
                  </div>

                  <div className="flex-grow-1">
                    <h6 className="mb-0 fw-semibold" style={{ fontSize: "0.85rem" }}>
                      {card.title}
                    </h6>
                    <small className="text-muted" style={{ fontSize: "0.75rem" }}>
                      {card.value} hrs
                    </small>
                  </div>

                  <h6 className={`mb-0 text-${card.bg} fw-bold`} style={{ fontSize: "0.9rem" }}>
                    {card.value}
                  </h6>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Timesheet Grid */}
        <div className="shadow-sm rounded border" style={{ overflow: "hidden" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "180px 90px 1fr",
              alignItems: "stretch",
            }}
          >
            {/* Left sticky: tasks */}
            <div style={{ position: "sticky", left: 0, zIndex: 30, background: "white" }}>
              <table className="table table-bordered mb-0 text-center" style={{ tableLayout: "fixed", width: "180px", marginBottom: 0 }}>
                <thead className="table-secondary sticky-top">
                  <tr>
                    <th className="bg-secondary text-white text-start px-3" style={{ minWidth: "180px", height: "65px", verticalAlign: "middle" }}>
                      Task
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tasks.length ? (
                    tasks.map((task, i) => (
                      <tr key={i}>
                        <td className="fw-normal text-start px-2">
                          <a
                            href="#"
                            className="text-decoration-none text-dark fw-normal"
                            onClick={(e) => {
                              e.preventDefault();
                              navigate(`/task/${task.id}`, { state: { task } });
                            }}
                          >
                            {task.title || `Task ${task.id}`}
                          </a>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className="text-muted text-center py-4">No tasks</td>
                    </tr>
                  )}
                </tbody>
                <tfoot className="fw-semibold table">
                  <tr className="table">
                    <td className="text-start px-3">Billable Total</td>
                  </tr>
                  <tr className="table">
                    <td className="text-start px-3">Non-Billable Total</td>
                  </tr>
                  <tr className="table">
                    <td className="text-start px-3">Daily Total</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Sticky second column: totals per task */}
            <div style={{ position: "sticky", left: 180, zIndex: 25, background: "white" }}>
              <table className="table table-bordered mb-0 text-center" style={{ tableLayout: "fixed", width: "90px", marginBottom: 0 }}>
                <thead className="table-primary sticky-top">
                  <tr>
                    <th className="bg-secondary text-white" style={{ minWidth: "90px", height: "65px", verticalAlign: "middle" }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {tasks.length ? (
                    tasks.map((task, i) => {
                      const rowTotal = days.reduce((sum, day) => sum + getHours(task.id, day), 0);
                      return (
                        <tr key={i}>
                          <td className="fw-semibold">{rowTotal || "0"}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td className="text-muted"></td>
                    </tr>
                  )}
                </tbody>
                <tfoot className="fw-semibold">
                  <tr className="table">
                    <td className="fw-semibold text-dark">{totalBillable}</td>
                  </tr>
                  <tr className="table">
                    <td className="fw-semibold text-dark">{totalNonBillable}</td>
                  </tr>
                  <tr className="table">
                    <td className="fw-semibold text-dark">{totalHours}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Scrollable days section */}
            <div style={{ overflowX: "auto" }}>
              <table className="table table-bordered mb-0 text-center" style={{ tableLayout: "fixed", minWidth: days.length * 70, marginBottom: 0 }}>
                <thead>
                  <tr>
                    {days.map((day, i) => {
                      const formatted = format(day, "yyyy-MM-dd");
                      const isHoliday = holidayDates.includes(formatted);
                      const holidayName = holidayMap[formatted];

                      return (
                        <th
                          key={i}
                          style={{
                            minWidth: "70px",
                            backgroundColor: isLeaveDay(day)
                              ? "#ffe6e6"          // 💚 light green for leave
                              : isHolidayColumn(day) ? "#d4f8d4" : isWeeklyHoliday(day) ? "#f7f7f7" : "",
                          }}
                          title={isHolidayColumn(day) ? holidayName : isWeeklyHoliday(day) ? "Weekly Holiday" : ""}
                        >
                          {format(day, "d")} <br />
                          <small className={isLeaveDay(day) ? "text-danger fw-semibold" : isHolidayColumn(day) ? "text-secondary fw-semibold" : isWeeklyHoliday(day) ? "text-secondary fw-semibold" : "text-muted"}>
                            {format(day, "EEE")}
                          </small>
                        </th>
                      );
                    })}
                  </tr>
                </thead>

                <tbody>
                  {tasks.length ? (
                    tasks.map((task, i) => (
                      <tr key={i}>
                        {days.map((day, j) => {
                          const logs = filteredWorklogs.filter(
                            (log) => log.taskId === task.id && isSameDay(new Date(log.date), day)
                          );

                          const billable = logs
                            .filter((log) => log.billingType === "billable")
                            .reduce((sum, log) => sum + log.hours, 0);

                          const nonBillable = logs
                            .filter((log) => log.billingType === "non-billable")
                            .reduce((sum, log) => sum + log.hours, 0);

                          return (
                            <td
                              key={j}
                              className="text-center align-middle"
                              style={{
                                backgroundColor: isLeaveDay(day)
                                  ? "#fff0f0"          // 💚 light green for leave
                                  : isHolidayColumn(day) ? "#d4f8d4" : isWeeklyHoliday(day) ? "#f7f7f7" : "",
                                cursor: "pointer",
                                whiteSpace: "pre-wrap",
                              }}
                              title={logs.map((l) => `${l.description || "No description"} (${l.hours}h)`).join("\n")}
                              onClick={() => handleCellClick(task, day)}
                            >
                              {/* If multiple logs, show stacked compact lines */}
                              {logs.length > 0 ? (
                                (() => {
                                  const totalHours = logs.reduce((sum, l) => sum + (l.hours || 0), 0);

                                  return (
                                    <div
                                      className="small fw-semibold"
                                      style={{ cursor: "pointer" }}
                                      onClick={() => {
                                        setCellLogs(logs);        // existing
                                        // setCellDate(format(date, "yyyy-MM-dd")); // existing
                                        setShowActionModal(true); // open your modal
                                      }}
                                    >
                                      {totalHours}
                                      <span className="ms-1 text-muted small">
                                        {logs.some(l => l.billingType === "billable")}
                                      </span>
                                    </div>
                                  );
                                })()
                              ) : (
                                <span style={{ visibility: "hidden" }}>0</span>
                              )}

                            </td>
                          );
                        })}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={days.length} className="text-muted py-4">No tasks</td>
                    </tr>
                  )}
                </tbody>

                <tfoot className="fw-normal">
                  {/* Billable totals */}
                  <tr className="table">
                    {days.map((day, i) => {
                      const billableTotal = tasks.reduce((sum, task) => sum + getHours(task.id, day, "billable"), 0);
                      const isHoliday = holidayDates.includes(format(day, "yyyy-MM-dd"));
                      const isWeekly = isWeeklyHoliday(day);
                      const isLeaveDay = leaveDates.includes(format(day, "yyyy-MM-dd"));
                      return (
                        <td key={i} style={{
                          backgroundColor: isLeaveDay
                            ? "#fff0f0"
                            : isHoliday ? "#d4f8d4" : isWeekly ? "#f7f7f7" : ""
                        }} title={isHoliday ? holidayMap[format(day, "yyyy-MM-dd")] : isWeekly ? "Weekly Holiday" : ""}>
                          {billableTotal ? <span className="fw-semibold text-dark">{billableTotal}</span> : <span style={{ visibility: "hidden" }}>0</span>}
                        </td>
                      );
                    })}
                  </tr>

                  {/* Non-billable totals */}
                  <tr className="table">
                    {days.map((day, i) => {
                      const raw = tasks.reduce(
                        (sum, task) => sum + getHours(task.id, day, "non-billable"),
                        0
                      );

                      const nonBillableTotal = Number(raw.toFixed(2));
                      console.log(nonBillableTotal);

                      const isHoliday = holidayDates.includes(format(day, "yyyy-MM-dd"));
                      const isWeekly = isWeeklyHoliday(day);
                      const isLeaveDay = leaveDates.includes(format(day, "yyyy-MM-dd"));
                      return (
                        <td key={i} style={{
                          backgroundColor: isLeaveDay
                            ? "#fff0f0"
                            : isHoliday ? "#d4f8d4" : isWeekly ? "#f7f7f7" : ""
                        }} title={isHoliday ? holidayMap[format(day, "yyyy-MM-dd")] : isWeekly ? "Weekly Holiday" : ""}>
                          {nonBillableTotal ? <span className="fw-semibold text-dark">{nonBillableTotal}</span> : <span style={{ visibility: "hidden" }}>0</span>}
                        </td>
                      );
                    })}
                  </tr>

                  {/* Column totals */}
                  <tr className="table">
                    {days.map((day, i) => {
                      const colTotal = tasks.reduce((sum, task) => sum + getHours(task.id, day), 0);
                      const isHoliday = holidayDates.includes(format(day, "yyyy-MM-dd"));
                      const isWeekly = isWeeklyHoliday(day);
                      const isLeaveDay = leaveDates.includes(format(day, "yyyy-MM-dd"));
                      return (
                        <td key={i} style={{
                          backgroundColor: isLeaveDay
                            ? "#fff0f0"
                            : isHoliday ? "#d4f8d4" : isWeekly ? "#f7f7f7" : ""
                        }} title={isHoliday ? holidayMap[format(day, "yyyy-MM-dd")] : isWeekly ? "Weekly Holiday" : ""}>
                          {colTotal ? <span className="fw-semibold text-dark">{colTotal}</span> : <span style={{ visibility: "hidden" }}>0</span>}
                        </td>
                      );
                    })}
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>

        <>
          <style>
            {`
      /* -------- TABLE STYLE (Jira-like) -------- */

      .jira-table {
        width: 100%;
        border-collapse: collapse;
      }

      .jira-table th {
        background: #f4f5f7;
        padding: 8px 10px;
        font-size: 13px;
        font-weight: 600;
        color: #030913ff;
        border-bottom: 1px solid #dfe1e6;
      }

      .jira-table td {
        padding: 10px 10px;
        font-size: 13px;
        color: #172b4d;
        border-bottom: 1px solid #ebecf0;
        vertical-align: middle;
      }

      .jira-table tr:hover {
        background: #fafbfc;
      }

      /* -------- Three dots button -------- */
      .jira-dots-btn {
        border: 1px solid transparent;
        background: transparent;
        font-size: 18px;
        padding: 2px 6px;
        cursor: pointer;
        border-radius: 4px;
        line-height: 1;
      }

      .jira-dots-btn:hover {
        border-color: #4c9aff;
        background: #e9f2ff;
      }

      /* -------- Dropdown style -------- */
      .jira-dropdown-menu {
        position: absolute;
        right: 0;
        margin-top: 5px;
        background: #fff;
        border: 1px solid #dfe1e6;
        border-radius: 6px;
        min-width: 140px;
        padding: 4px 0;
        box-shadow: 0 6px 10px rgba(0,0,0,0.15);
        display: none;
        z-index: 2000;
      }

      .jira-dropdown-menu.show {
        display: block;
      }

      .jira-dropdown-item {
        padding: 8px 14px;
        font-size: 13px;
        color: #172b4d;
        cursor: pointer;
      }

      .jira-dropdown-item:hover {
        background: #f4f5f7;
      }

      .jira-dropdown-item.text-danger:hover {
        background: #ffebe6;
      }

      /* Footer buttons */
      .jira-footer-btn {
        padding: 6px 16px;
        background: #126ef8ff;
        color: white;
        border: none;
        border-radius: 4px;
        font-size: 13px;
      }
    `}
          </style>

          {showActionModal && (
            <div className="modal show fade d-block" tabIndex="-1" role="dialog">
              {(() => {
                if (typeof window !== "undefined") {
                  window.toggleJiraDropdown = (id) => {
                    const isOpen = window.openDrop === id;
                    window.openDrop = isOpen ? null : id;

                    document.querySelectorAll(".jira-dropdown-menu").forEach((m) =>
                      m.classList.remove("show")
                    );

                    if (!isOpen) {
                      const menu = document.getElementById("jira-menu-" + id);
                      if (menu) menu.classList.add("show");
                    }
                  };

                  document.onclick = (e) => {
                    if (!e.target.closest(".dropdown-zone")) {
                      window.openDrop = null;
                      document.querySelectorAll(".jira-dropdown-menu").forEach((m) =>
                        m.classList.remove("show")
                      );
                    }
                  };
                }
              })()}

              <div className="modal-dialog modal-xl modal-dialog-centered">
                <div className="modal-content">

                  <div className="modal-header">
                    <h6>
                      Worklogs — {new Date(cellDate).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </h6>

                    <button className="btn-close" onClick={() => setShowActionModal(false)} />
                  </div>

                  <div className="modal-body">

                    <table className="jira-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Employee</th>
                          <th>Task</th>
                          <th>Description</th>
                          <th>Logged Hours</th>
                          <th style={{ width: "50px" }}>Actions</th>
                        </tr>
                      </thead>

                      <tbody>
                        {cellLogs.map((log) => (
                          <tr key={log.id}>
                            <td>
                              {new Date(log.date).toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              })}
                            </td>

                            <td>{(employeeName)}</td>
                            <td>{getTaskName(log.taskId)}</td>

                            <td>{log.description}</td>
                            <td>{log.hours}</td>

                            <td className="dropdown-zone" style={{ position: "relative" }}>
                              <button
                                className="jira-dots-btn"
                                onClick={() => window.toggleJiraDropdown(log.id)}
                              >
                                ⋮
                              </button>

                              <div
                                id={`jira-menu-${log.id}`}
                                className="jira-dropdown-menu"
                              >
                                <div className="jira-dropdown-item" onClick={() => editWorklogFromCell(log)}>
                                  Edit
                                </div>

                                <div className="jira-dropdown-item" onClick={() => openSplitModal(log)}>
                                  Split
                                </div>

                                <div
                                  className="jira-dropdown-item text-danger"
                                  onClick={() => deleteWorklog(log.id)}
                                >
                                  Delete
                                </div>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                  </div>

                  <div className="modal-footer">
                    <button
                      className="jira-footer-btn"
                      onClick={() =>
                        openCreateLogModal(
                          cellDate,
                          cellLogs.length > 0 ? cellLogs[0].taskId : null,
                          cellLogs.length > 0 ? getTaskName(cellLogs[0].taskId) : ""
                        )
                      }

                    >
                      Log Time
                    </button>


                    <button className="btn btn-link" onClick={() => setShowActionModal(false)}>
                      Close
                    </button>
                  </div>

                </div>
              </div>
            </div>
          )}
        </>




        {/* ---------------------- Split Modal ---------------------- */}
        {showSplitModal && splitOriginalLog && (
          <div className="modal show fade d-block" tabIndex="-1" role="dialog">
            <div className="modal-dialog modal-sm modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h6 className="modal-title">Split {splitOriginalLog.hours}h</h6>
                  <button type="button" className="btn-close" onClick={() => setShowSplitModal(false)}></button>
                </div>

                <div className="modal-body">
                  <div className="small text-muted mb-2">Original: {splitOriginalLog.description || "No description"}</div>
                  <div className="mb-2">
                    <label className="form-label small">Hours to split into new entry</label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      value={splitAmount}
                      onChange={(e) => setSplitAmount(e.target.value)}
                      min="0.5"
                      step="0.5"
                      placeholder="e.g. 1.5"
                    />
                    <div className="form-text small">Remaining hours will be left on original.</div>
                  </div>
                </div>

                <div className="modal-footer">
                  <button className="btn btn-secondary btn-sm" onClick={() => setShowSplitModal(false)}>Cancel</button>
                  <button className="btn btn-primary btn-sm" onClick={performSplit}>Split</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ---------------------- Create/Edit Log Modal ---------------------- */}
        {showModal && (
          <div className="modal show fade d-block" tabIndex="-1" role="dialog">
            <div className="modal-dialog modal-md modal-dialog-centered" role="document">
              <div className="modal-content">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    saveWorklog();
                  }}
                >
                  <div className="modal-header border-bottom bg-light">
                    <h5 className="modal-title fw-semibold"><i className="bi bi-clock me-2 text-primary"></i> Log Time</h5>
                    <button type="button" className="btn-close" onClick={() => { setShowModal(false); setEditWorklogId(null); }}></button>
                  </div>

                  <div className="modal-body">
                    <div className="mb-3">
                      <label className="form-label fw-semibold small text-muted">Task</label>
                      <div className="input-group">
                        <span className="input-group-text bg-white"><i className="bi bi-clipboard"></i></span>
                        <input type="text" className="form-control" value={modalData.taskTitle} readOnly />
                        <span className="input-group-text bg-white"><i className="bi bi-search"></i></span>
                      </div>
                    </div>

                    <div className="row g-3 mb-3">
                      <div className="col-6">
                        <label className="form-label fw-semibold small text-muted">Date</label>
                        <input type="date" className="form-control" value={modalData.date} onChange={(e) => setModalData({ ...modalData, date: e.target.value })} required />
                      </div>
                      <div className="col-6">
                        <label className="form-label fw-semibold small text-muted">Duration</label>

                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="e.g. 0.5 or 1h 30m"
                            value={durationRaw}
                            onChange={(e) => {
                              const raw = e.target.value;
                              setDurationRaw(raw);
                              const dec = parseDurationToDecimal ? parseDurationToDecimal(raw) : ensureDecimalHours(raw);
                              setDurationDecimal(dec);
                              setDurationReadable(formatDecimalToReadable(dec));
                            }}
                            onBlur={() => setDurationRaw(formatDecimalToReadable(durationDecimal))}
                            required
                          />


                          {/* <span className="input-group-text">h</span> */}
                        </div>
                      </div>

                    </div>

                    <div className="mb-3">
                      {/* <label className="form-label fw-semibold small text-muted">Billing Type</label> */}
                      <div className="d-flex gap-4 mt-1">
                        <div className="form-check">
                          <input className="form-check-input" type="radio" id="billable" name="billingType" value="billable" checked={modalData.billingType === "billable"} onChange={(e) => setModalData({ ...modalData, billingType: e.target.value })} />
                          <label className="form-check-label" htmlFor="billable">Billable</label>
                        </div>
                        <div className="form-check">
                          <input className="form-check-input" type="radio" id="nonBillable" name="billingType" value="non-billable" checked={modalData.billingType === "non-billable"} onChange={(e) => setModalData({ ...modalData, billingType: e.target.value })} />
                          <label className="form-check-label" htmlFor="nonBillable">Non-Billable</label>
                        </div>
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-semibold small text-muted">Description</label>
                      <textarea className="form-control" rows="3" value={modalData.description} onChange={(e) => setModalData({ ...modalData, description: e.target.value })} placeholder="Add worklog details..." required></textarea>
                    </div>
                  </div>

                  <div className="modal-footer border-top">
                    <button type="submit" className="btn btn-primary">{editWorklogId ? "Save" : "Log time"}</button>
                    <button type="button" className="btn btn-light" onClick={() => { setShowModal(false); setEditWorklogId(null); }}>Cancel</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default TimesheetGrid;
